import { ReactNode, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../app/store";
import {
  selectEffectiveRate,
  selectScenario,
  selectAnnualNet,
  selectMerchantFee,
  selectBaselineMerchantFee,
  selectAnnualBreakdown,
  selectBaselineNet,
  selectBaseEffectiveRate,
  selectBaselineIsMeasured,
  selectSelectedMonthEntry,
  selectMarkupAvailable,
  selectNetBasis,
  setNetBasis,
  selectTPV,
  selectTxns,
  selectHasPriceOverrides,
  selectShownNet,
  selectShownEffectiveRate,
  selectStatingToday,
} from "../../pricingToolSlice";
import {
  effectiveRateFormula,
  annualNetFormula,
  monthlyNetGroupBreakdown,
  FormulaExplanation,
  MonthlyNetExplanation,
} from "../../pricingFormulas";
import Spinner from "../shared/Spinner";
import InfoTooltip from "../shared/InfoTooltip";
import {
  eur,
  eurExact,
  pctFmt,
  monthLong,
  shortDate,
} from "../shared/formatters";
import styles from "../../PricingTool.module.css";

// Renders a FormulaExplanation: the expression, the exact variables plugged in,
// the result and an optional clarifying note.
function renderFormula(exp: FormulaExplanation): ReactNode {
  return (
    <div>
      <div style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", marginBottom: 8 }}>
        {exp.expression}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {exp.inputs.map((it) => (
          <div
            key={it.label}
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 18,
              fontSize: 12.5,
            }}
          >
            <span style={{ color: "rgba(0,0,0,0.55)" }}>{it.label}</span>
            <span
              style={{
                fontWeight: 600,
                color: "rgba(0,0,0,0.85)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {it.value}
            </span>
          </div>
        ))}
      </div>
      <div
        style={{
          marginTop: 8,
          paddingTop: 6,
          borderTop: "1px solid rgba(0,0,0,0.06)",
          display: "flex",
          justifyContent: "space-between",
          gap: 18,
          fontSize: 13,
        }}
      >
        <span style={{ color: "rgba(0,0,0,0.55)" }}>Result</span>
        <span
          style={{
            fontWeight: 700,
            color: "rgba(0,0,0,0.85)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {exp.result}
        </span>
      </div>
      {exp.note && (
        <div
          style={{
            marginTop: 8,
            fontSize: 11.5,
            lineHeight: 1.45,
            color: "rgba(0,0,0,0.45)",
          }}
        >
          {exp.note}
        </div>
      )}
    </div>
  );
}

// Renders the per-pricing-group revenue/cost breakdown that sums to monthly net.
function renderFeeBreakdown(exp: MonthlyNetExplanation): ReactNode {
  return (
    <div>
      <div style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", marginBottom: 8 }}>
        Σ group price × group volume
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {exp.groups.map((g) => (
          <div
            key={g.label}
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              fontSize: 12,
            }}
          >
            <span style={{ color: "rgba(0,0,0,0.6)" }}>
              {g.label}
              <span style={{ color: "rgba(0,0,0,0.38)", marginLeft: 6 }}>
                {g.revExpr}
              </span>
            </span>
            <span style={{ fontWeight: 600 }}>{eur(g.rev)}</span>
          </div>
        ))}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            fontSize: 12,
            borderTop: "1px solid rgba(0,0,0,0.08)",
            marginTop: 3,
            paddingTop: 5,
            fontWeight: 700,
          }}
        >
          <span>Merchant pays</span>
          <span>{eur(exp.totalRev)}</span>
        </div>
      </div>
    </div>
  );
}

function renderMonthlyBreakdown(exp: MonthlyNetExplanation): ReactNode {
  const detailRow = (
    label: string,
    expr: string,
    value: string,
    sign: "+" | "−"
  ) => (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 8,
        fontSize: 11,
        lineHeight: 1.6,
        color: "rgba(0,0,0,0.5)",
      }}
    >
      <span style={{ width: 52, flexShrink: 0 }}>{label}</span>
      <span style={{ flex: 1 }}>{expr}</span>
      <span style={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
        {sign}
        {value}
      </span>
    </div>
  );
  const basisRow = (label: string, value: string, note: string) => (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 8,
        fontSize: 10.5,
        lineHeight: 1.6,
        color: "rgba(0,0,0,0.4)",
      }}
    >
      <span style={{ width: 52, flexShrink: 0 }}>{label}</span>
      <span style={{ flex: 1 }}>
        <span
          style={{
            fontWeight: 600,
            color: "rgba(0,0,0,0.6)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {value}
        </span>
        <span style={{ marginLeft: 6 }}>· {note}</span>
      </span>
    </div>
  );

  return (
    <div>
      <div style={{ fontSize: 11, color: "rgba(0,0,0,0.45)", marginBottom: 4 }}>
        {exp.expression}
      </div>
      {exp.groups.map((g) => (
        <div
          key={g.label}
          style={{ padding: "9px 0", borderTop: "1px solid rgba(0,0,0,0.06)" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12.5,
              fontWeight: 600,
              color: "rgba(0,0,0,0.85)",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: g.color,
                flexShrink: 0,
              }}
            />
            <span style={{ flex: 1 }}>{g.label}</span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              {eurExact(g.net)}
            </span>
          </div>
          <div style={{ marginTop: 4, paddingLeft: 15 }}>
            {g.volumeBasis &&
              basisRow("Volume", g.volumeBasis.value, g.volumeBasis.note)}
            {g.txnBasis && basisRow("Txns", g.txnBasis.value, g.txnBasis.note)}
            {detailRow("Revenue", g.revExpr, eurExact(g.rev), "+")}
            {detailRow("Cost", g.costExpr, eurExact(g.cost), "−")}
          </div>
        </div>
      ))}
      <div
        style={{
          marginTop: 4,
          paddingTop: 9,
          borderTop: "1px solid rgba(0,0,0,0.12)",
          display: "flex",
          flexDirection: "column",
          gap: 5,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 11.5,
            color: "rgba(0,0,0,0.5)",
          }}
        >
          <span>Total revenue</span>
          <span>{eur(exp.totalRev, 2)}</span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 11.5,
            color: "rgba(0,0,0,0.5)",
          }}
        >
          <span>Total cost</span>
          <span>−{eur(exp.totalCost, 2)}</span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 13.5,
            fontWeight: 700,
            color: "#6B4EFF",
            marginTop: 2,
          }}
        >
          <span>Monthly net take</span>
          <span>{eur(exp.totalNet, 2)}</span>
        </div>
      </div>
    </div>
  );
}

export default function ProjectedNetTake() {
  const {
    channel,
    source,
    selectedBiz,
    industryGroup,
    mcc,
    city,
    areaCentre,
    areaBizCity,
    areaAddress,
    radiusKm,
    fetchStatus,
    apiRange,
    apiMonthly,
    areaBusinesses,
    partnerName,
    cohort,
    minMonthlyVolume,
  } = useSelector((s: RootState) => s.pricingTool);
  const { totalNet, rows } = useSelector(selectScenario);
  const effectiveRate = useSelector(selectEffectiveRate);
  const annualNet = useSelector(selectAnnualNet);
  const annualBreakdown = useSelector(selectAnnualBreakdown);
  const tpv = useSelector(selectTPV);
  const txns = useSelector(selectTxns);
  const baseNet = useSelector(selectBaselineNet);
  const merchantFee = useSelector(selectMerchantFee);
  const baseMerchantFee = useSelector(selectBaselineMerchantFee);
  const baseEffective = useSelector(selectBaseEffectiveRate);
  const baseIsMeasured = useSelector(selectBaselineIsMeasured);
  const selectedMonth = useSelector(selectSelectedMonthEntry);
  const markupAvailable = useSelector(selectMarkupAvailable);
  const netBasis = useSelector(selectNetBasis);
  const loading = fetchStatus === "loading";
  const dispatch = useDispatch();
  const afterMarkup = netBasis === "after";

  // Until a price is edited there is no proposal to compare against, so the
  // panel states today's measured figures instead of modelling its own.
  const hasProposal = useSelector(selectHasPriceOverrides);
  const showCompare = hasProposal && baseIsMeasured;
  const statingToday = useSelector(selectStatingToday);
  const shownRate = useSelector(selectShownEffectiveRate);
  const shownNet = useSelector(selectShownNet);
  const shownFee = statingToday ? baseMerchantFee : merchantFee;
  // The all-in rate the merchant is quoted: their whole fee over their volume,
  // which is the effective rate's counterpart on their side of the deal.
  const shownFeeRate = tpv > 0 ? (shownFee / tpv) * 100 : 0;

  const baseTitle = baseIsMeasured
    ? `What Klearly actually earned over this period, net of processing cost and ${afterMarkup ? "net of the reseller's and the ISV's cut" : "before partner markup"}.`
    : "Modelled from current list prices — no measured revenue for this selection.";

  const areaCount = areaBusinesses.length;
  const scopeParts: string[] = [
    channel === "offline" ? "In-person" : "Online",
    mcc ? industryGroup : industryGroup,
    source === "business" && selectedBiz ? selectedBiz.displayName : null,
    source === "area"
      ? (areaCentre === "business"
          ? areaBizCity
          : (areaAddress?.label ?? city)) +
        " · " +
        radiusKm +
        " km"
      : null,
    source === "area" && areaCount > 0
      ? `${areaCount} ${areaCount === 1 ? "business" : "businesses"}`
      : null,
    source === "area" && partnerName ? partnerName : null,
    source === "area" && cohort === "direct" ? "Direct merchants" : null,
    source === "area" && cohort === "partners" && !partnerName
      ? "Partner merchants"
      : null,
    // A floor changes what every figure below is an average of, so it belongs
    // with the other scope chips and not only in the control that sets it.
    source === "area" && minMonthlyVolume > 0
      ? `${eur(minMonthlyVolume)}+ / month`
      : null,
  ].filter(Boolean) as string[];

  const periodValue = selectedMonth
    ? monthLong(selectedMonth.month)
    : apiRange
      ? `${shortDate(apiRange.start)} – ${shortDate(apiRange.end)}`
      : null;
  const monthCount = apiMonthly?.length ?? 0;
  const perMerchant = source === "area" && areaCount > 1;
  const periodHintBody = selectedMonth
    ? `These figures use ${monthLong(selectedMonth.month)}'s real transaction volume and counts from the merchant's history.`
    : `These figures use the monthly average across ${monthCount > 0 ? `${monthCount} months` : "the period"} of transaction history${apiRange ? ` (${shortDate(apiRange.start)} – ${shortDate(apiRange.end)})` : ""}.${perMerchant ? ` Each one is a single merchant's average month — multiply by ${areaCount} for the whole cohort.` : ""}`;
  const periodHintAction = selectedMonth
    ? "Clear the month in the seasonality bar to use the whole period."
    : "Pick a month in the seasonality bar to model that month instead.";

  const effectiveFormula = renderFormula(effectiveRateFormula(totalNet, tpv));
  const groupBreakdown = monthlyNetGroupBreakdown(rows, tpv, txns);
  const monthlyFormula = renderMonthlyBreakdown(groupBreakdown);
  const merchantFeeFormula = renderFeeBreakdown(groupBreakdown);
  const annualFormula = renderFormula(annualNetFormula(annualBreakdown));

  const [open, setOpen] = useState<Record<string, boolean>>({});
  const toggle = (k: string) => setOpen((p) => ({ ...p, [k]: !p[k] }));
  const chevron = (k: string) => (
    <svg
      className={styles.net_take_chevron}
      data-open={open[k] ? "true" : "false"}
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );

  return (
    <div className={styles.net_take_card}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.9 }}>
          Projected net take
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {markupAvailable && (
            <div
              className={styles.net_take_basis_switch}
              role="group"
              aria-label="Net take basis"
            >
              {(["before", "after"] as const).map((basis) => (
                <button
                  key={basis}
                  type="button"
                  className={styles.net_take_basis_option}
                  data-active={netBasis === basis ? "true" : "false"}
                  aria-pressed={netBasis === basis}
                  onClick={() => dispatch(setNetBasis(basis))}
                >
                  {basis === "before" ? "Gross" : "After markup"}
                </button>
              ))}
            </div>
          )}
          <span className={styles.net_take_compare_pill}>
            {showCompare
              ? "Current → Proposed"
              : statingToday
                ? "Current"
                : "Proposed"}
          </span>
        </div>
      </div>
      <div className={styles.scope_chips}>
        {scopeParts.map((p, i) => (
          <span key={i} className={styles.scope_chip}>
            {p}
          </span>
        ))}
      </div>

      {periodValue && (
        <div className={styles.net_take_period}>
          <svg
            className={styles.net_take_period_icon}
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <span className={styles.net_take_period_label}>Based on</span>
          <span className={styles.net_take_period_value}>{periodValue}</span>
          <InfoTooltip
            header={selectedMonth ? "Single month" : "Whole period"}
            body={periodHintBody}
            hint={periodHintAction}
          >
            <span className={styles.net_take_period_help}>?</span>
          </InfoTooltip>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column" }}>
        <div className={styles.net_take_metric}>
          <div
            className={styles.net_take_metric_head}
            onClick={() => toggle("merchantFee")}
          >
            <div className={styles.net_take_row_label}>
              <div className={styles.net_take_row_title}>
                <span className={styles.net_take_row_primary}>
                  Merchant fee
                </span>
                {chevron("merchantFee")}
              </div>
              <span className={styles.net_take_row_sub}>
                what {perMerchant ? "the average merchant" : "the merchant"}{" "}
                pays / month · {pctFmt(shownFeeRate, 3)} of volume
              </span>
            </div>
            <div className={styles.net_take_row_value}>
              <div className={styles.net_take_main_val}>{eur(shownFee)}</div>
              {showCompare && (
                <div
                  className={styles.net_take_delta}
                  title="What this cohort is billed today at its measured prices."
                >
                  from {eur(baseMerchantFee)}{" "}
                  <span
                    style={{
                      // A rising bill is the merchant's loss, not a gain, so
                      // the colours are the opposite way round to the net take.
                      color:
                        merchantFee <= baseMerchantFee ? "#6effb4" : "#ff8a8c",
                      fontWeight: 600,
                    }}
                  >
                    {merchantFee >= baseMerchantFee ? "▲" : "▼"}{" "}
                    {eur(Math.abs(merchantFee - baseMerchantFee))}
                  </span>
                </div>
              )}
            </div>
          </div>
          {open.merchantFee && (
            <div className={styles.net_take_formula_panel}>
              {merchantFeeFormula}
            </div>
          )}
        </div>

        <div className={styles.net_take_metric}>
          <div
            className={styles.net_take_metric_head}
            onClick={() => toggle("effective")}
          >
            <div className={styles.net_take_row_label}>
              <div className={styles.net_take_row_title}>
                <span className={styles.net_take_row_primary}>
                  Effective rate
                </span>
                {chevron("effective")}
              </div>
              <span className={styles.net_take_row_sub}>
                net take as % of volume
                {afterMarkup ? ", after partner markup" : ""}
              </span>
            </div>
            <div className={styles.net_take_row_value}>
              <div className={styles.net_take_main_val}>
                {pctFmt(shownRate, 3)}
              </div>
              {showCompare && (
                <div className={styles.net_take_delta} title={baseTitle}>
                  from {pctFmt(baseEffective, 3)}{" "}
                  <span
                    style={{
                      color:
                        effectiveRate >= baseEffective ? "#6effb4" : "#ff8a8c",
                      fontWeight: 600,
                    }}
                  >
                    {effectiveRate >= baseEffective ? "▲" : "▼"}{" "}
                    {pctFmt(Math.abs(effectiveRate - baseEffective), 3)}
                  </span>
                </div>
              )}
            </div>
          </div>
          {open.effective && (
            <div className={styles.net_take_formula_panel}>
              {effectiveFormula}
            </div>
          )}
        </div>

        <div className={styles.net_take_metric}>
          <div
            className={styles.net_take_metric_head}
            onClick={() => toggle("monthly")}
          >
            <div className={styles.net_take_row_label}>
              <div className={styles.net_take_row_title}>
                <span className={styles.net_take_row_primary}>
                  Monthly net take
                </span>
                {chevron("monthly")}
              </div>
              <span className={styles.net_take_row_sub}>
                what Klearly keeps / month
                {perMerchant ? " per merchant" : ""}
                {afterMarkup ? ", partners paid" : ""}
              </span>
            </div>
            <div className={styles.net_take_row_value}>
              <div className={styles.net_take_main_val}>{eur(shownNet)}</div>
              {showCompare && (
                <div className={styles.net_take_delta} title={baseTitle}>
                  from {eur(baseNet)}{" "}
                  <span
                    style={{
                      color: totalNet >= baseNet ? "#6effb4" : "#ff8a8c",
                      fontWeight: 600,
                    }}
                  >
                    {totalNet >= baseNet ? "▲" : "▼"}{" "}
                    {eur(Math.abs(totalNet - baseNet))}
                  </span>
                </div>
              )}
            </div>
          </div>
          {open.monthly && (
            <div className={styles.net_take_formula_panel}>
              {monthlyFormula}
            </div>
          )}
        </div>

        <div className={styles.net_take_metric}>
          <div
            className={styles.net_take_metric_head}
            onClick={() => toggle("annual")}
          >
            <div className={styles.net_take_row_label}>
              <div className={styles.net_take_row_title}>
                <span className={styles.net_take_row_primary}>
                  Annual net take
                </span>
                {chevron("annual")}
              </div>
              <span className={styles.net_take_row_sub}>
                what Klearly keeps / year
                {perMerchant ? " per merchant" : ""}
                {afterMarkup ? ", partners paid" : ""}
              </span>
            </div>
            <div className={styles.net_take_row_value}>
              <div className={styles.net_take_main_val}>{eur(annualNet)}</div>
            </div>
          </div>
          {open.annual && (
            <div className={styles.net_take_formula_panel}>{annualFormula}</div>
          )}
        </div>
      </div>

      {loading && (
        <div className={styles.net_take_loader}>
          <Spinner size={24} light />
          <span style={{ fontSize: 13, color: "#fff", fontWeight: 500 }}>
            Fetching analytics…
          </span>
        </div>
      )}
    </div>
  );
}
